/**
 * Meridian **vector** basemap style builder — the OpenMapTiles-schema
 * street map rendered client-side, the MVP-friendly alternative to
 * self-hosting raster tiles in MinIO.
 *
 * # What this is
 *
 * Vector tiles are fetched **browser-direct** (REQ-UI-033) from the
 * deployment-advertised source (OpenFreeMap's keyless planet TileJSON
 * on the A testbed); MapLibre rasterises them on the GPU and labels
 * them **in English** client-side. This replaces the raster path's
 * pre-baked imagery + in-shader darkening with a real, theme-aware
 * cartographic style that carries **both** light and dark palettes in
 * the layer definitions (REQ-UI-040 / REQ-UI-045).
 *
 * # Provenance — adapted from OpenFreeMap "Positron"
 *
 * The layer taxonomy (which OpenMapTiles source-layers to draw, in what
 * order, with what zoom gates and filters) is adapted from the
 * OpenFreeMap Positron style (``https://tiles.openfreemap.org/styles/
 * positron``). Three deliberate departures from the upstream:
 *
 *   1. **Palette → Meridian design-system.** Positron's pastel greys are
 *      replaced with a muted palette derived from
 *      :data:`THEME_TOKENS.{light,dark}.map` + surface/border tokens so
 *      the basemap stays *ground* and the operator's data overlays
 *      (markers / detections / alerts / geometry) stay *figure* (agent
 *      rule 10; REQ-UI-046). Both themes are first-class.
 *   2. **Glyphs → BFF-served DM Sans.** Positron's ``glyphs`` (Noto via
 *      a CDN) is discarded for the same-origin BFF endpoint
 *      (:const:`DEFAULT_GLYPHS_BASE_URL` ⇒
 *      ``/api/glyphs/{fontstack}/{range}.pbf``; REQ-UI-035) and the
 *      committed ``["DM Sans Medium"]`` fontstack
 *      (:const:`VECTOR_BASEMAP_FONT`). No web-font CDN fetch (air-gap
 *      posture, CLAUDE.md §8.2).
 *   3. **No sprite (labels-only MVP).** Positron's ``sprite`` (POI /
 *      shield icons) is discarded — the BFF serves no sprite, and the
 *      MVP basemap is labels-only. Every symbol layer here is a text
 *      label with a halo; no ``icon-image``.
 *
 * # English labels (the load-bearing contract)
 *
 * Operators on these networks read English / Latin script, never the
 * local script of the mapped region. Every symbol layer's
 * ``text-field`` is :const:`ENGLISH_TEXT_FIELD` —
 * ``["coalesce", ["get","name:en"], ["get","name:latin"], ["get","name"]]``
 * — so a place/road resolves to its English name, then a Latin
 * transliteration, then (only as a last resort) the raw name. The
 * OpenMapTiles vector tiles carry both ``name:en`` and ``name:latin``
 * fields, so this is richer than Positron's ``name_en`` fallback.
 *
 * # Why a vector SOURCE ``url`` (TileJSON), not ``tiles[]``
 *
 * Unlike the raster path (which composes a concrete ``{z}/{x}/{y}`` tile
 * URL into ``tiles[]``), a MapLibre **vector** source takes the
 * descriptor's ``url_template`` as a **TileJSON ``url``**: MapLibre
 * fetches it and reads ``tiles[]`` / ``minzoom`` / ``maxzoom`` /
 * ``vector_layers`` from the TileJSON itself. For OpenFreeMap the
 * TileJSON (``…/planet``) and the ``.pbf`` tiles it points at are the
 * **same host** (``tiles.openfreemap.org``), so the per-deployment CSP
 * (REQ-UI-097) — auto-derived from ``MERIDIAN_BFF_TILE_URL_TEMPLATE`` —
 * covers both the TileJSON fetch and the tile fetches with one host
 * entry; no extra CSP host is needed (REQ-UI-097 notes vector sources
 * with same-origin glyphs need none).
 *
 * # Theme model — both palettes in-style, flip is per-layer paint
 *
 * The factory builds the **same** layer membership for light and dark;
 * only the ``paint`` differs. The runtime theme flip
 * (:func:`applyMapTheme`) therefore re-applies each basemap layer's
 * paint *in place* — never ``setStyle`` — so the imperatively-added
 * overlay layers survive (the same locked invariant the raster path
 * holds). :func:`vectorLayerPaints` exposes the per-layer paint for a
 * theme so the flip and the initial style share one source of truth.
 *
 * Cross-references:
 *   - REQ-UI-002 (MapLibre GL JS rendering).
 *   - REQ-UI-033 / REQ-UI-034 (browser-direct + retained BFF proxy).
 *   - REQ-UI-035 (BFF-only — glyphs same-origin; the one external host
 *     is the tile host, bounded by the CSP per REQ-UI-097).
 *   - REQ-UI-040 / REQ-UI-045 (paired light/dark themes).
 *   - REQ-UI-046 (status-colour legibility on the muted basemap).
 *   - ADR-016 §4 + 2026-06-10 amendment (config-driven browser-direct).
 *   - docs/engineering/detail/storyboards/shared/design-system.md.
 */
import type {
  BackgroundLayerSpecification,
  ExpressionSpecification,
  FillLayerSpecification,
  LayerSpecification,
  LineLayerSpecification,
  SymbolLayerSpecification,
  VectorSourceSpecification,
} from "maplibre-gl";
import type { ResolvedTheme } from "@/theme/tokens";
import { THEME_TOKENS } from "@/theme/tokens";
import {
  DEFAULT_GLYPHS_BASE_URL,
  glyphsUrlFromBase,
  MAP_BACKGROUND_LAYER_ID,
} from "@/map/map-constants";

/**
 * MapLibre source ID for the vector basemap source. Distinct from the
 * raster :const:`MAP_BASE_SOURCE_ID` so a deployment that advertises
 * *both* a raster and a vector option never collides source ids, and so
 * the theme-flip can probe the active basemap kind by which source id is
 * present.
 */
export const VECTOR_BASEMAP_SOURCE_ID = "meridian-vector";

/**
 * The fontstack every vector label requests — the SDF atlas committed
 * under ``packages/meridian_bff/.../static/glyphs/DM Sans Medium/``
 * (ranges 0-511, covering English + Latin) and served same-origin at
 * ``/api/glyphs/DM Sans Medium/{range}.pbf`` (REQ-UI-035). Reused from
 * the cluster-count layer's fontstack — NOT Noto (no CDN web-font;
 * air-gap posture, CLAUDE.md §8.2).
 */
export const VECTOR_BASEMAP_FONT: readonly string[] = ["DM Sans Medium"];

/**
 * The English label rule applied to **every** symbol layer's
 * ``text-field``.
 *
 * ``name:en`` (English) → ``name:latin`` (Latin transliteration) →
 * ``name`` (raw, possibly local script — last resort only). Operators
 * read English / Latin, never the local script of the mapped region, so
 * a Tokyo road reads "Koshu-kaido", not "甲州街道". OpenMapTiles tiles
 * carry both ``name:en`` and ``name:latin``.
 *
 * Typed as a MapLibre ``coalesce`` expression so the style-spec accepts
 * it as a data-driven ``text-field`` value.
 */
export const ENGLISH_TEXT_FIELD: ExpressionSpecification = [
  "coalesce",
  ["get", "name:en"],
  ["get", "name:latin"],
  ["get", "name"],
];

/**
 * The muted basemap palette for one theme — derived from the
 * design-system tokens so the street map stays *ground* under the
 * operator's data overlays (REQ-UI-046, agent rule 10).
 *
 * These are *basemap* colours (water / land / roads / boundaries /
 * label text + halo), distinct from the status/marker palette
 * (``marker-styling.ts``). Each is a literal hex — MapLibre's WebGL
 * paint can't resolve CSS ``var()`` — chosen to sit a step *below* the
 * UI chrome surfaces so markers pop. Light values lean on warm-neutral
 * greys; dark values lean on the near-black ``surface`` ramp.
 */
interface VectorPalette {
  /** Land / earth fill (the default ground behind everything). */
  readonly land: string;
  /** Water bodies + waterways. */
  readonly water: string;
  /** Waterway lines (rivers, streams). */
  readonly waterwayLine: string;
  /** Residential / built-up land tint. */
  readonly residential: string;
  /** Parks / green landcover. */
  readonly park: string;
  /** Wood / forest landcover. */
  readonly wood: string;
  /** Building fill. */
  readonly building: string;
  /** Building outline. */
  readonly buildingOutline: string;
  /** Motorway / trunk road casing (the darker outer stroke). */
  readonly roadMajorCasing: string;
  /** Motorway / trunk road fill (the lighter inner stroke). */
  readonly roadMajor: string;
  /** Minor / residential road fill. */
  readonly roadMinor: string;
  /** Path / track fill. */
  readonly roadPath: string;
  /** Rail line. */
  readonly rail: string;
  /** Administrative boundary line. */
  readonly boundary: string;
  /** Aeroway (runway / taxiway / apron). */
  readonly aeroway: string;
  /** Primary label text. */
  readonly labelText: string;
  /** Water-feature label text (rivers, lakes — tinted toward the water). */
  readonly labelTextWater: string;
  /** Label halo (so text reads over any underlying fill). */
  readonly labelHalo: string;
}

/**
 * Light-theme basemap palette — warm-neutral greys anchored on the
 * design-system light ``surface`` / ``border`` / ``map`` tokens.
 */
const LIGHT_PALETTE: VectorPalette = {
  land: THEME_TOKENS.light.map.bgFrom, // #ebedf2 — matches the canvas
  water: "#cdd6e4", // a half-step bluer than border.default, muted
  waterwayLine: "#b3bdd0",
  residential: "#eef0f4",
  park: "#dde8df", // faint green, desaturated
  wood: "#d4e0d6",
  building: "#e3e5ec",
  buildingOutline: "#d3d6e0",
  roadMajorCasing: THEME_TOKENS.light.border.default, // #c8ccd8
  roadMajor: "#ffffff",
  roadMinor: "#f4f5f8",
  roadPath: "#e6e8ee",
  rail: "#c8ccd8",
  boundary: THEME_TOKENS.light.border.strong, // #9199ad — dashed admin lines
  aeroway: "#e8eaf0",
  labelText: THEME_TOKENS.light.text.primary, // #1a1d2b
  labelTextWater: "#4a5a86",
  labelHalo: "#f5f6f8", // surface.0 — bright halo for light text
};

/**
 * Dark-theme basemap palette — near-black ``surface`` ramp so the map
 * reads as a muted dark canvas and status markers retain WCAG-AA
 * contrast (REQ-UI-046). Mirrors the structure of :data:`LIGHT_PALETTE`
 * exactly (same keys) so the theme-flip re-paints every layer 1:1.
 */
const DARK_PALETTE: VectorPalette = {
  land: THEME_TOKENS.dark.map.bgFrom, // #181c27 — matches the canvas
  water: "#10141e", // darker than land so water reads as recessed
  waterwayLine: "#28304a",
  residential: "#1c2130",
  park: "#16241c", // faint desaturated green, near-black
  wood: "#13201a",
  building: "#1f2437", // surface.2
  buildingOutline: "#2e3450", // border.default
  roadMajorCasing: "#3a4060", // border-ish casing under the road
  roadMajor: "#3e445f", // lifted road so it reads on near-black
  roadMinor: "#2a3046",
  roadPath: "#252a3a",
  rail: "#2e3450",
  boundary: THEME_TOKENS.dark.border.strong, // #4f566b
  aeroway: "#222a3c",
  labelText: THEME_TOKENS.dark.text.primary, // #e8eaf0
  labelTextWater: "#7e93c8",
  labelHalo: THEME_TOKENS.dark.surface[0], // #0f1117 — dark halo for light text
};

/** Resolve the palette for a theme. */
function paletteFor(theme: ResolvedTheme): VectorPalette {
  return theme === "dark" ? DARK_PALETTE : LIGHT_PALETTE;
}

/**
 * The returned style fragment — a ``sources`` map + a ``layers`` array
 * the caller composes into a full :class:`StyleSpecification` (the
 * raster path's :func:`buildMapStyle` does the same for its raster
 * source/layers).
 */
export interface VectorBasemap {
  readonly sources: Record<string, VectorSourceSpecification>;
  readonly layers: LayerSpecification[];
}

/**
 * Build the Meridian vector basemap (source + layers) for a theme.
 *
 * @param theme - The resolved map theme (``"light"`` / ``"dark"``).
 * @param tileJsonUrl - The descriptor's ``url_template`` — a **TileJSON
 *     URL** (MapLibre reads tiles[]/zoom/vector_layers from it). For the
 *     A testbed this is OpenFreeMap's ``…/planet``.
 * @param glyphsBaseUrl - Base path for the BFF glyphs endpoint.
 *     Defaults to :const:`DEFAULT_GLYPHS_BASE_URL`.
 * @returns The ``{ sources, layers }`` fragment + (via the caller) the
 *     shared ``glyphs`` URL.
 *
 * Cross-references:
 *   - REQ-UI-002 / REQ-UI-033 / REQ-UI-040 / REQ-UI-045 / REQ-UI-046.
 *   - ADR-016 amended 2026-06-10.
 */
export function buildVectorBasemap(
  theme: ResolvedTheme,
  tileJsonUrl: string,
  glyphsBaseUrl: string = DEFAULT_GLYPHS_BASE_URL,
): VectorBasemap {
  const p = paletteFor(theme);
  // glyphsBaseUrl is consumed by the caller (buildMapStyle) for the
  // style-level ``glyphs`` key; referenced here only so the signature
  // documents the dependency and a future per-source glyph override has
  // a seam. (No-op read to satisfy noUnusedParameters without a comment
  // pragma.)
  void glyphsUrlFromBase(glyphsBaseUrl);

  const source: VectorSourceSpecification = {
    type: "vector",
    // TileJSON URL — NOT a tiles[] array. MapLibre fetches it and reads
    // tiles[]/minzoom/maxzoom/vector_layers. Same host serves the .pbf
    // tiles (OpenFreeMap), so one CSP host covers both (REQ-UI-097).
    url: tileJsonUrl,
    // OpenMapTiles attribution is legally required (ODbL); surfaced by
    // the compact AttributionControl. The descriptor's own attribution
    // is also threaded by the caller onto the source — this is a
    // sensible default for the OpenFreeMap planet source.
    attribution: "© OpenMapTiles © OpenStreetMap contributors",
  };

  // Symbol-layer factory — applies the two load-bearing label
  // invariants (the English ``text-field`` + the DM Sans ``text-font``)
  // and the theme's text colour + halo uniformly, so a caller supplies
  // only the layer-specific layout (placement, size, transform) and any
  // zoom/filter ``extra``. ``layout`` is the symbol layout *without* the
  // two injected keys (injected below); typed loosely as a partial of
  // the spec's layout to keep the call sites terse.
  type SymLayout = Omit<
    NonNullable<SymbolLayerSpecification["layout"]>,
    "text-field" | "text-font"
  >;
  type SymExtra = Partial<
    Omit<SymbolLayerSpecification, "id" | "type" | "source" | "source-layer" | "layout" | "paint">
  >;
  const sym = (
    id: string,
    sourceLayer: string,
    layout: SymLayout,
    textColor: string,
    extra?: SymExtra,
  ): SymbolLayerSpecification => ({
    id,
    type: "symbol",
    source: VECTOR_BASEMAP_SOURCE_ID,
    "source-layer": sourceLayer,
    layout: {
      ...layout,
      // The two load-bearing label invariants applied uniformly:
      "text-field": ENGLISH_TEXT_FIELD,
      "text-font": [...VECTOR_BASEMAP_FONT],
    },
    paint: {
      "text-color": textColor,
      "text-halo-color": p.labelHalo,
      "text-halo-width": 1.4,
      "text-halo-blur": 0.6,
    },
    ...extra,
  });

  const layers: LayerSpecification[] = [
    // ── Background ───────────────────────────────────────────────────
    {
      id: MAP_BACKGROUND_LAYER_ID,
      type: "background",
      paint: {
        "background-color":
          theme === "dark" ? THEME_TOKENS.dark.map.bgFrom : THEME_TOKENS.light.map.bgFrom,
      },
    } satisfies BackgroundLayerSpecification,

    // ── Land / landcover / landuse (fills, drawn first → bottom) ─────
    {
      id: "landcover-wood",
      type: "fill",
      source: VECTOR_BASEMAP_SOURCE_ID,
      "source-layer": "landcover",
      filter: ["==", ["get", "class"], "wood"],
      paint: { "fill-color": p.wood, "fill-opacity": 0.6 },
    } satisfies FillLayerSpecification,
    {
      id: "landuse-residential",
      type: "fill",
      source: VECTOR_BASEMAP_SOURCE_ID,
      "source-layer": "landuse",
      filter: ["==", ["get", "class"], "residential"],
      paint: { "fill-color": p.residential },
    } satisfies FillLayerSpecification,
    {
      id: "park",
      type: "fill",
      source: VECTOR_BASEMAP_SOURCE_ID,
      "source-layer": "park",
      paint: { "fill-color": p.park, "fill-opacity": 0.7 },
    } satisfies FillLayerSpecification,

    // ── Water ────────────────────────────────────────────────────────
    {
      id: "water",
      type: "fill",
      source: VECTOR_BASEMAP_SOURCE_ID,
      "source-layer": "water",
      paint: { "fill-color": p.water, "fill-antialias": true },
    } satisfies FillLayerSpecification,
    {
      id: "waterway",
      type: "line",
      source: VECTOR_BASEMAP_SOURCE_ID,
      "source-layer": "waterway",
      paint: {
        "line-color": p.waterwayLine,
        "line-width": ["interpolate", ["linear"], ["zoom"], 8, 0.5, 16, 2],
      },
    } satisfies LineLayerSpecification,

    // ── Aeroway (airports) ───────────────────────────────────────────
    {
      id: "aeroway-area",
      type: "fill",
      source: VECTOR_BASEMAP_SOURCE_ID,
      "source-layer": "aeroway",
      filter: ["==", ["geometry-type"], "Polygon"],
      paint: { "fill-color": p.aeroway },
    } satisfies FillLayerSpecification,
    {
      id: "aeroway-line",
      type: "line",
      source: VECTOR_BASEMAP_SOURCE_ID,
      "source-layer": "aeroway",
      filter: ["==", ["geometry-type"], "LineString"],
      paint: {
        "line-color": p.aeroway,
        "line-width": ["interpolate", ["exponential", 1.5], ["zoom"], 11, 3, 17, 24],
      },
    } satisfies LineLayerSpecification,

    // ── Buildings ────────────────────────────────────────────────────
    {
      id: "building",
      type: "fill",
      source: VECTOR_BASEMAP_SOURCE_ID,
      "source-layer": "building",
      minzoom: 12,
      paint: {
        "fill-color": p.building,
        "fill-outline-color": p.buildingOutline,
        "fill-opacity": ["interpolate", ["linear"], ["zoom"], 12, 0, 13, 1],
      },
    } satisfies FillLayerSpecification,

    // ── Roads (casing under inner, minor under major) ────────────────
    {
      id: "road-path",
      type: "line",
      source: VECTOR_BASEMAP_SOURCE_ID,
      "source-layer": "transportation",
      filter: ["==", ["get", "class"], "path"],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": p.roadPath,
        "line-width": ["interpolate", ["exponential", 1.2], ["zoom"], 13, 1, 20, 8],
      },
    } satisfies LineLayerSpecification,
    {
      id: "road-minor",
      type: "line",
      source: VECTOR_BASEMAP_SOURCE_ID,
      "source-layer": "transportation",
      minzoom: 8,
      filter: ["in", ["get", "class"], ["literal", ["minor", "service", "track"]]],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": p.roadMinor,
        "line-width": ["interpolate", ["exponential", 1.55], ["zoom"], 13, 1.8, 20, 20],
      },
    } satisfies LineLayerSpecification,
    {
      id: "rail",
      type: "line",
      source: VECTOR_BASEMAP_SOURCE_ID,
      "source-layer": "transportation",
      minzoom: 13,
      filter: ["==", ["get", "class"], "rail"],
      layout: { "line-join": "round" },
      paint: {
        "line-color": p.rail,
        "line-width": ["interpolate", ["exponential", 1.3], ["zoom"], 16, 2, 20, 6],
      },
    } satisfies LineLayerSpecification,
    {
      id: "road-major-casing",
      type: "line",
      source: VECTOR_BASEMAP_SOURCE_ID,
      "source-layer": "transportation",
      minzoom: 8,
      filter: [
        "in",
        ["get", "class"],
        ["literal", ["primary", "secondary", "tertiary", "trunk", "motorway"]],
      ],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": p.roadMajorCasing,
        "line-width": ["interpolate", ["exponential", 1.4], ["zoom"], 6, 0.5, 20, 26],
      },
    } satisfies LineLayerSpecification,
    {
      id: "road-major",
      type: "line",
      source: VECTOR_BASEMAP_SOURCE_ID,
      "source-layer": "transportation",
      minzoom: 8,
      filter: [
        "in",
        ["get", "class"],
        ["literal", ["primary", "secondary", "tertiary", "trunk", "motorway"]],
      ],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": p.roadMajor,
        "line-width": ["interpolate", ["exponential", 1.4], ["zoom"], 6, 0.5, 20, 18],
      },
    } satisfies LineLayerSpecification,

    // ── Boundaries (above land/roads so admin lines read) ────────────
    {
      id: "boundary-state",
      type: "line",
      source: VECTOR_BASEMAP_SOURCE_ID,
      "source-layer": "boundary",
      filter: ["in", ["get", "admin_level"], ["literal", [3, 4, 5, 6]]],
      minzoom: 4,
      paint: {
        "line-color": p.boundary,
        "line-dasharray": [2, 2],
        "line-width": ["interpolate", ["linear"], ["zoom"], 4, 0.6, 12, 1.6],
      },
    } satisfies LineLayerSpecification,
    {
      id: "boundary-country",
      type: "line",
      source: VECTOR_BASEMAP_SOURCE_ID,
      "source-layer": "boundary",
      filter: ["==", ["get", "admin_level"], 2],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": p.boundary,
        "line-width": ["interpolate", ["linear"], ["zoom"], 3, 0.8, 12, 2.4],
      },
    } satisfies LineLayerSpecification,

    // ── Labels (symbols, drawn last → top of the basemap) ────────────
    // Water labels.
    sym(
      "water-name",
      "water_name",
      { "text-size": ["interpolate", ["linear"], ["zoom"], 0, 10, 8, 13], "text-max-width": 6 },
      p.labelTextWater,
    ),
    // Road names (line-placed).
    sym(
      "transportation-name",
      "transportation_name",
      {
        "symbol-placement": "line",
        "text-size": ["interpolate", ["linear"], ["zoom"], 13, 11, 16, 13],
        "text-rotation-alignment": "map",
      },
      p.labelText,
      { minzoom: 12 },
    ),
    // Aerodrome (airport) labels.
    sym(
      "aerodrome-label",
      "aerodrome_label",
      { "text-size": 11, "text-max-width": 9, "text-anchor": "top", "text-offset": [0, 0.6] },
      p.labelText,
      { minzoom: 10 },
    ),
    // Place labels — small (suburbs / villages / towns).
    sym(
      "place-other",
      "place",
      {
        "text-size": ["interpolate", ["linear"], ["zoom"], 8, 10, 14, 14],
        "text-max-width": 8,
        "text-transform": "uppercase",
        "text-letter-spacing": 0.05,
      },
      p.labelText,
      {
        minzoom: 8,
        filter: [
          "in",
          ["get", "class"],
          ["literal", ["suburb", "neighbourhood", "hamlet", "village", "town"]],
        ],
      },
    ),
    // Place labels — cities.
    sym(
      "place-city",
      "place",
      {
        "text-size": ["interpolate", ["exponential", 1.2], ["zoom"], 4, 12, 11, 20],
        "text-max-width": 8,
      },
      p.labelText,
      { minzoom: 3, filter: ["==", ["get", "class"], "city"] },
    ),
    // Place labels — states (mid zoom).
    sym(
      "place-state",
      "place",
      {
        "text-size": ["interpolate", ["linear"], ["zoom"], 5, 10, 8, 14],
        "text-max-width": 9,
        "text-transform": "uppercase",
        "text-letter-spacing": 0.1,
      },
      p.labelText,
      { minzoom: 4, maxzoom: 8, filter: ["==", ["get", "class"], "state"] },
    ),
    // Place labels — countries (top of the label stack).
    sym(
      "place-country",
      "place",
      {
        "text-size": ["interpolate", ["linear"], ["zoom"], 2, 11, 6, 18],
        "text-max-width": 7,
        "text-transform": "uppercase",
        "text-letter-spacing": 0.05,
      },
      p.labelText,
      { maxzoom: 9, filter: ["==", ["get", "class"], "country"] },
    ),
  ];

  return { sources: { [VECTOR_BASEMAP_SOURCE_ID]: source }, layers };
}

/**
 * The ids of every layer :func:`buildVectorBasemap` emits, in draw
 * order. Exported so :func:`applyMapTheme`'s vector branch re-paints
 * exactly its own basemap layers on a theme flip (and never an overlay
 * layer). Kept in lock-step with the builder by a test that asserts the
 * built ids equal this set.
 */
export const VECTOR_BASEMAP_LAYER_IDS: readonly string[] = [
  MAP_BACKGROUND_LAYER_ID,
  "landcover-wood",
  "landuse-residential",
  "park",
  "water",
  "waterway",
  "aeroway-area",
  "aeroway-line",
  "building",
  "road-path",
  "road-minor",
  "rail",
  "road-major-casing",
  "road-major",
  "boundary-state",
  "boundary-country",
  "water-name",
  "transportation-name",
  "aerodrome-label",
  "place-other",
  "place-city",
  "place-state",
  "place-country",
];

/**
 * The per-layer ``paint`` objects for a theme, keyed by layer id — the
 * single source of truth the runtime theme-flip
 * (:func:`applyMapTheme` vector branch) consumes to re-colour each
 * basemap layer in place (never ``setStyle``, so overlays survive).
 *
 * Built by running :func:`buildVectorBasemap` for the theme and reading
 * back each layer's ``paint`` — so the flip can never drift from the
 * initial style (they're the same builder).
 *
 * @param theme - The resolved map theme.
 * @param tileJsonUrl - The TileJSON url (only used to satisfy the
 *     builder; the paints don't depend on it).
 * @returns ``{ [layerId]: paint }`` for every basemap layer that has a
 *     ``paint`` (all but a paintless layer, of which there are none).
 *
 * Cross-references:
 *   - REQ-UI-040 / REQ-UI-045 (paired themes; layer-preserving flip).
 */
export function vectorLayerPaints(
  theme: ResolvedTheme,
  tileJsonUrl: string,
): Record<string, Record<string, unknown>> {
  const { layers } = buildVectorBasemap(theme, tileJsonUrl);
  const paints: Record<string, Record<string, unknown>> = {};
  for (const layer of layers) {
    const paint = (layer as { paint?: Record<string, unknown> }).paint;
    if (paint !== undefined) {
      paints[layer.id] = paint;
    }
  }
  return paints;
}
